import React, { useEffect, useRef, useState } from "react";
import { TextField, Autocomplete } from "@mui/material";
import { useJsApiLoader } from "@react-google-maps/api";

type Prediction = google.maps.places.AutocompletePrediction;

type LocationValue = {
  address: string;
  lat: number;
  lng: number;
};

type Props = {
  value: LocationValue;
  onChange: (value: LocationValue) => void;
  error?: boolean;
  helperText?: string;
  onBlur?: () => void;
  disabled?: boolean;
};

const GOOGLE_MAP_LIBRARIES: "places"[] = ["places"];

const ControlledPlaceAutocomplete: React.FC<Props> = ({ value, onChange, error, helperText, onBlur, disabled }) => {
  const [options, setOptions] = useState<Prediction[]>([]);
  const [inputValue, setInputValue] = useState<string>(value?.address || "");
  const autocompleteServiceRef = useRef<google.maps.places.AutocompleteService | null>(null);

  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: String(import.meta.env.VITE_GOOGLE_MAPS_API_KEY),
    libraries: GOOGLE_MAP_LIBRARIES,
  });

  useEffect(() => {
    if (isLoaded && !autocompleteServiceRef.current) {
      autocompleteServiceRef.current = new google.maps.places.AutocompleteService();
    }
  }, [isLoaded]);

  useEffect(() => {
    setInputValue(value?.address || "");
  }, [value?.address]);

  useEffect(() => {
    if (inputValue && autocompleteServiceRef.current) {
      autocompleteServiceRef.current.getPlacePredictions(
        {
          input: inputValue,
          componentRestrictions: { country: "mx" },
        },
        (predictions) => {
          setOptions(predictions || []);
        },
      );
    } else {
      setOptions([]);
    }
  }, [inputValue]);

  const handleSelect = (option: Prediction | null) => {
    if (!option) return;

    const placesService = new google.maps.places.PlacesService(document.createElement("div"));

    placesService.getDetails(
      {
        placeId: option.place_id,
        fields: ["formatted_address", "geometry"],
      },
      (place, status) => {
        if (
          status === google.maps.places.PlacesServiceStatus.OK &&
          place?.formatted_address &&
          place.geometry?.location
        ) {
          const lat = place.geometry.location.lat();
          const lng = place.geometry.location.lng();

          const locationData: LocationValue = {
            address: place.formatted_address,
            lat,
            lng,
          };

          onChange(locationData);
        }
      },
    );
  };

  return (
    <Autocomplete
      fullWidth
      freeSolo
      options={options}
      getOptionLabel={(option) => (typeof option === "string" ? option : option.description)}
      onChange={(_, newValue) => {
        if (typeof newValue === "string") {
          onChange({ address: newValue, lat: 0, lng: 0 });
          return;
        }

        handleSelect(newValue as Prediction);
      }}
      inputValue={inputValue}
      onInputChange={(_, newInputValue, reason) => {
        setInputValue(newInputValue);

        if (reason === "input" || reason === "clear") {
          onChange({ address: newInputValue, lat: 0, lng: 0 });
        }
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          label="Dirección"
          disabled={disabled}
          error={error}
          helperText={helperText}
          onBlur={onBlur}
        />
      )}
    />
  );
};

export default ControlledPlaceAutocomplete;
